import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { COST_CATEGORIES, BR_ZONES, fmtKm, fmtMin } from '../utils/mockState';
import { CostModel, loadCostModel, saveCostModel, fetchCostModelApi, saveCostModelApi, routeProfitability } from '../utils/costModel';
import api from '../api/client';
import { RouteEntity } from '../types';
import './BI.css';

function fmtMoney(v: number, decimals = 2) {
  return (v || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function marginBadge(m: number) {
  const pct = Math.abs(m * 100).toFixed(1).replace('.', ',');
  const cls = m > 0.2 ? 'high' : m > 0.05 ? 'mid' : m > 0 ? 'low' : 'neg';
  return (
    <span className={`margin-badge ${cls}`}>
      {m > 0 ? '' : '−'}
      {pct}%
    </span>
  );
}

interface PricingSim {
  stops: number;
  weight: number;
  zoneId: string;
  timeWindow: string;
  urgency: string;
  targetMargin: number;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function BI() {
  const { showToast } = useToast();
  const [routes, setRoutes] = useState<RouteEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const didLoadData = useRef(false);
  const [savingCostModel, setSavingCostModel] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [costModel, setCostModel] = useState<CostModel>(loadCostModel());

  const [sim, setSim] = useState<PricingSim>({
    stops: 12,
    weight: 800,
    zoneId: 'zona_sul',
    timeWindow: 'comercial',
    urgency: 'normal',
    targetMargin: 0.22
  });

  useEffect(() => {
    // Evita a segunda consulta do StrictMode durante o desenvolvimento.
    if (didLoadData.current) return;
    didLoadData.current = true;

    Promise.all([
      api.get<RouteEntity[]>('/routes'),
      fetchCostModelApi()
    ])
      .then(([routesRes, costRes]) => {
        setRoutes(routesRes.data);
        if (routesRes.data.length > 0) setSelectedRouteId(routesRes.data[0].id);
        if (costRes) setCostModel(costRes);
      })
      .catch(() => showToast('Erro ao carregar dados do BI.', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withProfit = useMemo(
    () =>
      routes.map((r) => ({
        route: r,
        ...routeProfitability(r.distanceM / 1000, r.durationS / 60, r.stops?.length || 0, costModel)
      })),
    [routes, costModel]
  );

  const totalRev = withProfit.reduce((s, x) => s + x.revenue, 0);
  const totalCost = withProfit.reduce((s, x) => s + x.cost.total, 0);
  const totalProfit = totalRev - totalCost;
  const avgMargin = withProfit.length ? withProfit.reduce((s, x) => s + x.margin, 0) / withProfit.length : 0;
  const totalStopsCount = routes.reduce((s, r) => s + (r.stops?.length || 0), 0);
  const avgCostPerStop = totalStopsCount > 0 ? totalCost / totalStopsCount : 0;
  const profitableCount = withProfit.filter((x) => x.margin > 0.05).length;
  const negativeM = withProfit.filter((x) => x.margin < 0).length;

  const selectedEntry = withProfit.find((x) => x.route.id === selectedRouteId) || withProfit[0];
  const selectedRoute = selectedEntry?.route;
  const sel = selectedEntry;
  const catValues = sel
    ? {
        fuel: sel.cost.fuel,
        driver: sel.cost.driver,
        depreciation: sel.cost.depreciation,
        maintenance: sel.cost.maintenance,
        tolls: sel.cost.tolls,
        overhead: sel.cost.overhead
      }
    : null;

  // Pricing simulation (calculadora "what-if" para novas propostas, não depende de dados históricos)
  const p = useMemo(() => {
    const zone = BR_ZONES.find((z) => z.id === sim.zoneId) || BR_ZONES[0];
    const cm = costModel;
    const estKm = sim.stops * 4.2 * zone.kmMult;
    const durationHrs = (estKm / 32 + (sim.stops * 3.5) / 60) * zone.complexity;
    const weightFactor = 1 + Math.max(0, (sim.weight - 500) / 2000);
    const twFactor = { comercial: 1.0, livre: 0.95, restrita: 1.15, noturna: 1.25 }[sim.timeWindow] || 1.0;
    const urgFactor = { normal: 1.0, expressa: 1.35, agendada: 0.9 }[sim.urgency] || 1.0;

    const fuel = estKm * cm.fuelConsumption * cm.fuelPrice * weightFactor;
    const driver = durationHrs * cm.driverHourlyCost * twFactor;
    const depreciation = estKm * cm.depreciationPerKm;
    const maintenance = estKm * cm.maintenancePerKm;
    const tolls = estKm * cm.tollPerKm;
    const subtotal = fuel + driver + depreciation + maintenance + tolls;
    const overhead = subtotal * cm.overheadPct;
    const cost = (subtotal + overhead) * urgFactor;
    const minPrice = cost * 1.05;
    const suggestedPrice = cost * (1 + sim.targetMargin);
    const marketMid = cost * 1.28;
    const marketMin = marketMid * 0.85;
    const marketMax = marketMid * 1.15;
    return { estKm, durationHrs, cost, minPrice, suggestedPrice, marketMid, marketMin, marketMax, sim, zone };
  }, [sim, costModel]);

  const pricePerStop = p.suggestedPrice / Math.max(1, p.sim.stops);
  const marketPercentile = Math.min(100, Math.max(0, ((p.suggestedPrice - p.marketMin) / (p.marketMax - p.marketMin)) * 100));

  let rec: { cls: 'ok' | 'warn' | 'err'; lead: string; d: string };
  if (p.suggestedPrice < p.marketMin * 0.95) {
    rec = { cls: 'ok', lead: 'Excelente competitividade', d: 'Preço sugerido está abaixo do piso de mercado. Alta chance de fechamento.' };
  } else if (p.suggestedPrice > p.marketMax * 1.05) {
    rec = { cls: 'err', lead: 'Preço acima do mercado', d: 'Sua sugestão está acima do teto praticado. Considere ajustar a margem.' };
  } else if (p.suggestedPrice > p.marketMid) {
    rec = { cls: 'warn', lead: 'Preço no topo da faixa', d: 'Fique atento à negociação comercial com o cliente.' };
  } else {
    rec = { cls: 'ok', lead: 'Preço competitivo e saudável', d: 'Alinhado com o mercado e com margem líquida sustentável.' };
  }

  const updateSim = (patch: Partial<PricingSim>) => setSim((s) => ({ ...s, ...patch }));

  const handleUpdateCostModel = (key: keyof CostModel, value: number) => {
    const next = { ...costModel, [key]: value };
    setCostModel(next);
    saveCostModel(next);
  };

  const handleSaveCostModelToDb = async () => {
    setSavingCostModel(true);
    try {
      const saved = await saveCostModelApi(costModel);
      setCostModel(saved);
      showToast('Parâmetros de custo salvos no banco de dados com sucesso!', 'success');
    } catch {
      showToast('Erro ao salvar parâmetros no banco de dados.', 'error');
    } finally {
      setSavingCostModel(false);
    }
  };

  const sortedProfitable = useMemo(() => withProfit.slice().sort((a, b) => b.margin - a.margin), [withProfit]);

  const handleExportSheet = () => {
    if (withProfit.length === 0) {
      showToast('Nenhuma rota para exportar.', 'error');
      return;
    }
    const header = ['rota', 'distancia_km', 'duracao_min', 'paradas', 'custo_total', 'receita_estimada', 'lucro', 'margem_pct'];
    const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const rows = withProfit.map((x) => [
      x.route.name,
      (x.route.distanceM / 1000).toFixed(1),
      (x.route.durationS / 60).toFixed(0),
      String(x.route.stops?.length || 0),
      x.cost.total.toFixed(2),
      x.revenue.toFixed(2),
      x.profit.toFixed(2),
      (x.margin * 100).toFixed(1)
    ]);
    const csv = [header, ...rows].map((r) => r.map(escape).join(',')).join('\n');
    downloadCsv('custos_rentabilidade.csv', csv);
    showToast('Planilha financeira exportada!', 'success');
  };

  if (loading) {
    return (
      <Layout>
        <div className="empty"><p>Carregando dados de custos...</p></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Custos & Rentabilidade</h1>
          <p className="page-lede">
            Descubra o custo real de cada entrega, quais rotas dão prejuízo silencioso e precifique novos contratos com precisão.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={handleExportSheet}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7,10 12,15 17,10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exportar planilha
          </button>
        </div>
      </div>

      <p style={{ color: 'var(--text-4)', fontSize: 11.5, marginTop: -12, marginBottom: 20 }}>
        Custo calculado a partir da distância/duração reais das rotas × parâmetros de custo abaixo. Receita estimada com base no preço médio por parada configurado (não há valor de contrato registrado por rota ainda).
      </p>

      {/* 4 BI KPI Cards */}
      <div className="bi-kpi-grid">
        <div className="card" style={{ padding: 20, borderLeft: '4px solid var(--teal-800)' }}>
          <div style={{ color: 'var(--text-3)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Custo médio por entrega
          </div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700, marginTop: 4, letterSpacing: '-0.02em' }}>
            R$ {fmtMoney(avgCostPerStop)}
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 6 }}>
            {totalStopsCount} entregas · {withProfit.length} rotas analisadas
          </div>
        </div>

        <div className="card" style={{ padding: 20, borderLeft: '4px solid var(--teal-500)' }}>
          <div style={{ color: 'var(--text-3)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Receita estimada
          </div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700, marginTop: 4, letterSpacing: '-0.02em' }}>
            R$ {fmtMoney(totalRev, 0)}
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 6 }}>
            Preço médio configurado: <b>R$ {fmtMoney(costModel.avgPricePerStop)}/parada</b>
          </div>
        </div>

        <div className="card" style={{ padding: 20, borderLeft: '4px solid var(--yellow)' }}>
          <div style={{ color: 'var(--text-3)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Lucro líquido
          </div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: totalProfit > 0 ? '#0d7a6c' : 'var(--danger)', letterSpacing: '-0.02em' }}>
            R$ {fmtMoney(totalProfit, 0)}
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 6 }}>
            Margem média da operação: {marginBadge(avgMargin)}
          </div>
        </div>

        <div className="card" style={{ padding: 20, borderLeft: '4px solid var(--amber)' }}>
          <div style={{ color: 'var(--text-3)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Rentabilidade das rotas
          </div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700, marginTop: 4, letterSpacing: '-0.02em' }}>
            {profitableCount}<span style={{ fontSize: 14, color: 'var(--text-3)', fontWeight: 500 }}>/ {withProfit.length}</span>
          </div>
          <div style={{ fontSize: 12, marginTop: 6 }}>
            {negativeM > 0 ? (
              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>⚠ {negativeM} rotas com prejuízo</span>
            ) : withProfit.length > 0 ? (
              <span style={{ color: '#0d7a6c', fontWeight: 600 }}>✓ Nenhuma rota deficitária</span>
            ) : (
              <span style={{ color: 'var(--text-3)' }}>Sem rotas ainda</span>
            )}
          </div>
        </div>
      </div>

      {/* 2-Column: Cost Anatomy & Profitability Rankings */}
      <div className="bi-cost-grid">
        {/* Cost Anatomy Card */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Anatomia do custo por rota</div>
              <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 2 }}>
                Selecione uma rota para ver a composição real do que ela custou de operar
              </div>
            </div>
            {routes.length > 0 && (
              <select
                className="input"
                value={selectedRouteId}
                onChange={(e) => setSelectedRouteId(e.target.value)}
                style={{ width: 220, padding: '7px 10px', fontSize: 13 }}
              >
                {routes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} · {r.stops?.length || 0} paradas
                  </option>
                ))}
              </select>
            )}
          </div>

          {sel && catValues && selectedRoute ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '12px 0', borderBottom: '1px solid var(--line-2)' }}>
                <div>
                  <div style={{ color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>
                    Custo total desta rota
                  </div>
                  <div className="mono" style={{ fontSize: 24, fontWeight: 700, color: 'var(--teal-800)', marginTop: 2 }}>
                    R$ {fmtMoney(sel.cost.total)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    {fmtKm(selectedRoute.distanceM / 1000)} km · {fmtMin(selectedRoute.durationS / 60)}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {marginBadge(sel.margin)} · Lucro R$ {fmtMoney(sel.profit, 0)}
                  </div>
                </div>
              </div>

              <div style={{ height: 22, borderRadius: 6, display: 'flex', overflow: 'hidden', margin: '16px 0', background: 'var(--paper)' }}>
                {COST_CATEGORIES.map((cat) => {
                  const val = catValues[cat.key as keyof typeof catValues];
                  const pct = (val / sel.cost.total) * 100;
                  return (
                    <div
                      key={cat.key}
                      style={{ background: cat.color, width: `${pct}%`, height: '100%', transition: 'width 0.3s' }}
                      title={`${cat.label}: R$ ${fmtMoney(val)} (${pct.toFixed(1)}%)`}
                    />
                  );
                })}
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {COST_CATEGORIES.map((cat) => {
                    const val = catValues[cat.key as keyof typeof catValues];
                    const pct = (val / sel.cost.total) * 100;
                    return (
                      <tr key={cat.key} style={{ borderBottom: '1px solid var(--line-2)' }}>
                        <td style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: cat.color }}></span>
                          <span style={{ fontWeight: 600 }}>{cat.label}</span>
                          <span style={{ color: 'var(--text-4)', fontSize: 11 }}>({cat.formula})</span>
                        </td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 600, padding: '8px 0' }}>
                          R$ {fmtMoney(val)}
                        </td>
                        <td className="mono" style={{ textAlign: 'right', color: 'var(--text-3)', padding: '8px 0', width: 60 }}>
                          {pct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ fontWeight: 700, background: 'var(--paper-2)' }}>
                    <td style={{ padding: '10px 0' }}>Custo operacional total</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '10px 0' }}>R$ {fmtMoney(sel.cost.total)}</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '10px 0' }}>100%</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--teal-500-soft)', padding: 14, borderRadius: 10, marginTop: 14 }}>
                <div>
                  <div style={{ color: 'var(--teal-800)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Custo por entrega</div>
                  <div style={{ color: 'var(--teal-900)', fontSize: 12, marginTop: 2 }}>
                    R$ {fmtMoney(sel.cost.total)} ÷ {selectedRoute.stops?.length || 0} paradas
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 24, fontWeight: 700, color: 'var(--teal-800)' }}>
                  R$ {fmtMoney(sel.cost.perStop)}
                </div>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Nenhuma rota cadastrada ainda para analisar custos.</p>
          )}
        </div>

        {/* Right Rankings: Profitable vs Deficit */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 20 }}>
            <h4 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 12 }}>
              Top Rotas Mais Rentáveis
            </h4>
            {sortedProfitable.filter((x) => x.margin > 0).slice(0, 4).map((x, i) => (
              <div
                key={x.route.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px dashed var(--line-2)', cursor: 'pointer' }}
                onClick={() => setSelectedRouteId(x.route.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#e9c46a' : 'var(--paper)', color: i === 0 ? '#a97f22' : 'var(--text-2)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>
                    {i + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{x.route.name}</div>
                    <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{x.route.stops?.length || 0} paradas · {fmtKm(x.route.distanceM / 1000)} km</div>
                  </div>
                </div>
                <div>{marginBadge(x.margin)}</div>
              </div>
            ))}
            {sortedProfitable.filter((x) => x.margin > 0).length === 0 && (
              <p style={{ color: 'var(--text-3)', fontSize: 12.5 }}>Nenhuma rota rentável ainda.</p>
            )}
          </div>

          <div className="card" style={{ padding: 20 }}>
            <h4 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--danger)', marginBottom: 12 }}>
              Rotas com Menor Margem / Atenção
            </h4>
            {sortedProfitable.filter((x) => x.margin <= 0.15).slice(0, 3).map((x) => (
              <div
                key={x.route.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px dashed var(--line-2)', cursor: 'pointer' }}
                onClick={() => setSelectedRouteId(x.route.id)}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{x.route.name}</div>
                  <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{x.route.stops?.length || 0} paradas · {fmtKm(x.route.distanceM / 1000)} km</div>
                </div>
                <div>{marginBadge(x.margin)}</div>
              </div>
            ))}
            {sortedProfitable.filter((x) => x.margin <= 0.15).length === 0 && (
              <p style={{ color: 'var(--text-3)', fontSize: 12.5 }}>Nenhum alerta de margem baixa.</p>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Engine Simulator */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>Simulador de Precificação de Contratos</h3>
          <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
            Simule o custo e o preço ideal para novas propostas comerciais com base no seu modelo operacional configurado abaixo.
          </p>
        </div>

        <div className="bi-sim-grid">
          <div>
            <div className="field-row">
              <div className="field">
                <label>Paradas estimadas</label>
                <input type="number" className="input" value={sim.stops} onChange={(e) => updateSim({ stops: parseInt(e.target.value, 10) || 1 })} min={1} />
              </div>
              <div className="field">
                <label>Carga total (kg)</label>
                <input type="number" className="input" value={sim.weight} onChange={(e) => updateSim({ weight: parseInt(e.target.value, 10) || 0 })} min={0} />
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label>Região / Zona de entrega</label>
                <select className="input" value={sim.zoneId} onChange={(e) => updateSim({ zoneId: e.target.value })}>
                  {BR_ZONES.map((z) => (
                    <option key={z.id} value={z.id}>{z.name} ({z.kmMult}x km)</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Janela de horário</label>
                <select className="input" value={sim.timeWindow} onChange={(e) => updateSim({ timeWindow: e.target.value })}>
                  <option value="comercial">Comercial (08h–18h)</option>
                  <option value="restrita">Restrita (janela &lt; 4h)</option>
                  <option value="noturna">Noturna (18h–22h)</option>
                  <option value="livre">Livre (24h)</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Margem líquida desejada:</span>
                <span className="mono" style={{ color: 'var(--teal-800)', fontWeight: 700 }}>{Math.round(sim.targetMargin * 100)}%</span>
              </label>
              <input
                type="range" min="5" max="45" value={Math.round(sim.targetMargin * 100)}
                onChange={(e) => updateSim({ targetMargin: parseInt(e.target.value, 10) / 100 })}
                style={{ width: '100%', accentColor: 'var(--teal-800)' }}
              />
            </div>
          </div>

          <div style={{ background: 'var(--paper-2)', padding: 20, borderRadius: 12, border: '1px solid var(--line-2)' }}>
            <div className="summary-metric">
              <span className="k">Distância calculada</span>
              <span className="v mono">{p.estKm.toFixed(1)} km</span>
            </div>
            <div className="summary-metric">
              <span className="k">Custo operacional estimado</span>
              <span className="v mono">R$ {fmtMoney(p.cost)}</span>
            </div>
            <div className="summary-metric">
              <span className="k">Preço mínimo viável</span>
              <span className="v mono" style={{ color: 'var(--danger)' }}>R$ {fmtMoney(p.minPrice)}</span>
            </div>

            <div style={{ background: 'linear-gradient(135deg, var(--teal-800), var(--teal-700))', color: '#fff', padding: 16, borderRadius: 10, margin: '14px 0' }}>
              <div style={{ color: '#7cd3c6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                Preço Sugerido (Margem {Math.round(sim.targetMargin * 100)}%)
              </div>
              <div className="mono" style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>R$ {fmtMoney(p.suggestedPrice)}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                R$ {fmtMoney(pricePerStop)} por parada · {sim.stops} entregas
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', background: '#fff', borderRadius: 8, border: '1px solid var(--line-2)' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)' }}>Mercado (referência):</span>
              <div style={{ flex: 1, height: 6, background: 'linear-gradient(90deg, #c94a3f 0%, #e9c46a 50%, #2a9d8f 100%)', borderRadius: 3, position: 'relative' }}>
                <div style={{ position: 'absolute', top: -5, left: `${marketPercentile}%`, width: 2, height: 16, background: '#0b1b22' }} />
              </div>
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>R$ {p.marketMin.toFixed(0)}–{p.marketMax.toFixed(0)}</span>
            </div>

            <div className={`pricing-recommendation ${rec.cls}`} style={{ marginTop: 12, padding: 12, borderRadius: 8 }}>
              <div className="lead">{rec.lead}</div>
              <div className="d">{rec.d}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Editable Cost Model Parameters */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Parâmetros do Modelo Operacional de Custo</h3>
            <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: '4px 0 0' }}>
              Valores persistidos no PostgreSQL vinculados à sua empresa e aplicados a todas as análises financeiras.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleSaveCostModelToDb}
            disabled={savingCostModel}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {savingCostModel ? (
              <span>Salvando...</span>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>Salvar no Banco</span>
              </>
            )}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
          <div className="cost-model-item">
            <div className="lbl">Preço do Combustível (R$/L)</div>
            <div className="input-line">
              <input type="number" step="0.01" value={costModel.fuelPrice} onChange={(e) => handleUpdateCostModel('fuelPrice', parseFloat(e.target.value) || 0)} />
              <span className="u">R$/L</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Consumo Médio (L/km)</div>
            <div className="input-line">
              <input type="number" step="0.01" value={costModel.fuelConsumption} onChange={(e) => handleUpdateCostModel('fuelConsumption', parseFloat(e.target.value) || 0)} />
              <span className="u">L/km</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Hora-Motorista (R$/h)</div>
            <div className="input-line">
              <input type="number" step="0.5" value={costModel.driverHourlyCost} onChange={(e) => handleUpdateCostModel('driverHourlyCost', parseFloat(e.target.value) || 0)} />
              <span className="u">R$/h</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Depreciação por km</div>
            <div className="input-line">
              <input type="number" step="0.01" value={costModel.depreciationPerKm} onChange={(e) => handleUpdateCostModel('depreciationPerKm', parseFloat(e.target.value) || 0)} />
              <span className="u">R$/km</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Manutenção por km</div>
            <div className="input-line">
              <input type="number" step="0.01" value={costModel.maintenancePerKm} onChange={(e) => handleUpdateCostModel('maintenancePerKm', parseFloat(e.target.value) || 0)} />
              <span className="u">R$/km</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Pedágio Médio por km</div>
            <div className="input-line">
              <input type="number" step="0.01" value={costModel.tollPerKm} onChange={(e) => handleUpdateCostModel('tollPerKm', parseFloat(e.target.value) || 0)} />
              <span className="u">R$/km</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Overhead / Indiretos (%)</div>
            <div className="input-line">
              <input type="number" step="0.01" min="0" max="1" value={costModel.overheadPct} onChange={(e) => handleUpdateCostModel('overheadPct', parseFloat(e.target.value) || 0)} />
              <span className="u">dec. (ex: 0.15)</span>
            </div>
          </div>

          <div className="cost-model-item">
            <div className="lbl">Preço Médio por Parada</div>
            <div className="input-line">
              <input type="number" step="0.5" value={costModel.avgPricePerStop} onChange={(e) => handleUpdateCostModel('avgPricePerStop', parseFloat(e.target.value) || 0)} />
              <span className="u">R$/parada</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
